'use client';
import { useEffectEvent } from "@floating-ui/react/utils";
import { useMotionValue, useSpring } from "motion/react";
import * as motion from "motion/react-client";
import { HTMLAttributes, ReactNode, startTransition, useEffect, useRef } from "react";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import { getMediaQueryMinWidthForColumnCount } from "./ProjectsSection";

interface MagnetProps extends HTMLAttributes<HTMLDivElement> {
	readonly children: ReactNode;
	readonly padding?: number;
	readonly disabled?: boolean;
	readonly magnetStrength?: number;
	readonly springConfig?: {
		damping: number;
		stiffness: number;
	};
	readonly wrapperClassName?: string;
	readonly innerClassName?: string;
}

const SPRING_CONFIG_DEFAULT = { damping: 6, stiffness: 90 } as const;

export function ProjectCardMagneticEffect({children, ...props}: MagnetProps) {
	const shouldUse = useMediaQuery(getMediaQueryMinWidthForColumnCount(2));
	if (!shouldUse) return children;

	return <ProjectCardMagneticEffectInternal {...props}>{children}</ProjectCardMagneticEffectInternal>;
}

function ProjectCardMagneticEffectInternal({
	children,
	padding = 96,
	disabled = false,
	magnetStrength = 200,
	springConfig = SPRING_CONFIG_DEFAULT,
	wrapperClassName = "",
	innerClassName = "",
	...props
}: MagnetProps) {
	// Use motion values instead of React state
	const x = useMotionValue(0);
	const y = useMotionValue(0);

	// Add spring physics for smooth animation
	const springX = useSpring(x, springConfig);
	const springY = useSpring(y, springConfig);

	const magnetRef = useRef<HTMLDivElement>(null);

	const lastKnownMousePosRef = useRef<{ x: number; y: number }>({x: 999, y: 999});
	const isQueuedRef = useRef(false);

	const reposition = useEffectEvent((e: Event | MouseEvent | {clientX?: number, clientY?: number}) => {
		//console.log("Mouse move event", e);

		if (isQueuedRef.current) return;
		isQueuedRef.current = true;

		let gotToTransition = false;
		try {
			if (!magnetRef.current) return;

			if ('clientX' in e && 'clientY' in e) lastKnownMousePosRef.current = { x: e.clientX, y: e.clientY };
			const mousePos = lastKnownMousePosRef.current;

			const { left, top, width, height } = magnetRef.current.getBoundingClientRect();
			const centerX = left + width / 2;
			const centerY = top + height / 2;

			const distX = Math.abs(centerX - mousePos.x);
			const distY = Math.abs(centerY - mousePos.y);

			//console.log("Mouse move", { mousePos, centerX, centerY, distX, distY });

			gotToTransition = true;
			startTransition(() => {
				try {
					if (distX < width / 2 + padding && distY < height / 2 + padding) {
						const offsetX = (mousePos.x - centerX) / magnetStrength * Math.max(1, height / width);
						const offsetY = (mousePos.y - centerY) / magnetStrength * Math.max(1, width / height);

						// So. Funny story.
						// For some reason, only in Firefox, the springiness will compound when moving the mouse around
						// within the magnetic field, resulting in wild oscillations that get stronger and stronger.
						//
						// This is NOT related to a bug in Chrome where transform seems to be ignored in getBoundingClientRect (it is,
						// but that doesn't cause this issue; I already fixed it). I have no idea why Firefox behaves like this.
						//
						// Either way, This clamping stops those oscillations from becoming too extreme, allowing for this neat
						// stacking without getting to the point where the card takes over a minute to return to its resting position.
						const oscillationClamp = Math.abs(padding * 1.5);
						x.set(Math.max(-oscillationClamp, Math.min(oscillationClamp, offsetX)));
						y.set(Math.max(-oscillationClamp, Math.min(oscillationClamp, offsetY)));
					} else {
						x.set(0);
						y.set(0);
					}
				} finally {
					isQueuedRef.current = false;
				}
			});
		} finally {
			if (!gotToTransition) isQueuedRef.current = false;
		}
	});

	useEffect(() => {
		if (disabled) {
			x.set(0);
			y.set(0);
			return;
		}

		const handleMouseLeave = (evt: MouseEvent) => {
			if (evt.relatedTarget === null) reposition({clientX: Infinity, clientY: Infinity});
		};

		window.addEventListener("mousemove", reposition);
		window.addEventListener("scroll", reposition, true);
		window.addEventListener("resize", reposition);
		document.addEventListener("mouseleave", handleMouseLeave);


		return () => {
			window.removeEventListener("mousemove", reposition);
			window.removeEventListener("scroll", reposition);
			window.removeEventListener("resize", reposition);
			document.removeEventListener("mouseleave", handleMouseLeave);
		};
	}, [reposition, disabled, x, y]);

	return <div
		ref={magnetRef}
		className={wrapperClassName}
		style={{ position: "relative", display: "inline-block" }}
		{...props}
	>
		<motion.div
			className={innerClassName}
			style={{
				x: springX,
				y: springY,
				willChange: "transform",
			}}
			transition={{ type: "spring", duration: 0.8, ...springConfig }}
		>
			{children}
		</motion.div>
	</div>;
};
